import { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { LayoutDashboard, ListChecks, Ticket, Plug, Languages, LogOut, Scissors, Palette, Megaphone, Coins, Image as ImageIcon, FileText, Type } from "lucide-react";
import AdminDashboard from "./AdminDashboard";
import AdminAssessments from "./AdminAssessments";
import AdminComplimentary from "./AdminComplimentary";
import AdminIntegrations from "./AdminIntegrations";
import AdminLanguages from "./AdminLanguages";
import AdminSalons from "./AdminSalons";
import AdminBranding from "./AdminBranding";
import AdminCampaigns from "./AdminCampaigns";
import AdminPlans from "./AdminPlans";
import AdminImages from "./AdminImages";
import AdminQuestions from "./AdminQuestions";
import AdminLandingCopy from "./AdminLandingCopy";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/assessments", icon: ListChecks, label: "Assessments" },
  { to: "/admin/complimentary", icon: Ticket, label: "Complimentary Access" },
  { to: "/admin/plans", icon: Coins, label: "Plans & Pricing" },
  { to: "/admin/salons", icon: Scissors, label: "Hairtu Salons" },
  { to: "/admin/branding", icon: Palette, label: "KT Milano Branding" },
  { to: "/admin/images", icon: ImageIcon, label: "Assessment Images" },
  { to: "/admin/landing-copy", icon: Type, label: "Landing Page Text" },
  { to: "/admin/questions", icon: FileText, label: "Questions Text" },
  { to: "/admin/campaigns", icon: Megaphone, label: "Brand Partnerships" },
  { to: "/admin/languages", icon: Languages, label: "Languages" },
  { to: "/admin/integrations", icon: Plug, label: "Integrations" },
];

export default function AdminApp() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/me`).then((r) => setMe(r.data))
      .catch(() => nav("/admin/login"))
      .finally(() => setLoading(false));
  }, [nav]);

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`); } catch {}
    nav("/admin/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6F6B66]">…</div>;
  if (!me) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row" data-testid="admin-app">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r kt-line bg-[#F8F6F2] p-6 flex flex-col">
        <div className="text-[11px] kt-tracking-wider uppercase text-[#383633] mb-1">KT Milano</div>
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-8">Admin panel</div>
        <nav className="flex-1 flex flex-row lg:flex-col gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2 text-sm whitespace-nowrap ${
                  isActive ? "bg-white text-[#383633] kt-shadow border kt-line" : "text-[#6F6B66] hover:bg-white/60"
                }`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="pt-6 mt-6 border-t kt-line hidden lg:block">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Signed in</div>
          <div className="text-sm text-[#383633] mt-1" data-testid="admin-email">{me.email}</div>
          <button onClick={logout} data-testid="logout-btn"
            className="mt-4 inline-flex items-center gap-2 text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 lg:p-10">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="assessments" element={<AdminAssessments />} />
          <Route path="complimentary" element={<AdminComplimentary />} />
          <Route path="plans" element={<AdminPlans />} />
          <Route path="salons" element={<AdminSalons />} />
          <Route path="branding" element={<AdminBranding />} />
          <Route path="images" element={<AdminImages />} />
          <Route path="landing-copy" element={<AdminLandingCopy />} />
          <Route path="questions" element={<AdminQuestions />} />
          <Route path="campaigns" element={<AdminCampaigns />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="languages" element={<AdminLanguages />} />
        </Routes>
      </main>
    </div>
  );
}
